import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { paidAccessApi } from "@/lib/api/paid-access";
import { PaidAccessEntitlementRows } from "./PaidAccessEntitlementRows";
import { useRevokeEntitlement } from "./useRevokeEntitlement";
import type { PaidAccessAdminSnapshot } from "./paid-access-admin.types";
import { Receipt, Settings2, Users } from "lucide-react";

interface PaidAccessResourceAdminCardProps {
  digitalResourceId: string;
  resourceTitle: string;
  /** When set, card uses shared data instead of fetching on mount. */
  adminSnapshot?: PaidAccessAdminSnapshot;
  onAdminRefresh?: () => void;
}

export function PaidAccessResourceAdminCard({
  digitalResourceId,
  resourceTitle,
  adminSnapshot,
  onAdminRefresh,
}: PaidAccessResourceAdminCardProps) {
  const usesParentSnapshot = adminSnapshot !== undefined;
  const [loading, setLoading] = useState(!usesParentSnapshot);
  const [snapshot, setSnapshot] = useState<PaidAccessAdminSnapshot | null>(adminSnapshot ?? null);

  const reload = () => {
    if (onAdminRefresh) {
      setLoading(true);
      onAdminRefresh();
      return;
    }
    setLoading(true);
    Promise.all([
      paidAccessApi.getPricingRules(digitalResourceId),
      paidAccessApi.listPurchases(digitalResourceId),
      paidAccessApi.listEntitlements(digitalResourceId),
    ])
      .then(([rules, purchases, entitlements]) => {
        setSnapshot({ rules, purchases, entitlements });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (usesParentSnapshot && adminSnapshot) {
      setSnapshot(adminSnapshot);
      setLoading(false);
    }
  }, [adminSnapshot, usesParentSnapshot]);

  useEffect(() => {
    if (usesParentSnapshot) {
      return;
    }
    reload();
  }, [digitalResourceId, usesParentSnapshot]);

  const { revoke, revokingId, ConfirmDialogComponent } = useRevokeEntitlement(reload);

  const ruleCount = snapshot?.rules.length ?? 0;
  const purchaseCount = snapshot?.purchases.length ?? 0;
  const entitlementTotal = snapshot?.entitlements.length ?? 0;
  const entitlements = snapshot?.entitlements.slice(0, 5) ?? [];

  const qs = `resourceId=${encodeURIComponent(digitalResourceId)}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Receipt className="h-5 w-5 text-primary" />
          Paid digital access
        </CardTitle>
        <CardDescription>
          Patron portal pricing, purchases, and entitlements for &quot;{resourceTitle}&quot;.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="rounded-md border px-2 py-1">
                {ruleCount} pricing rule{ruleCount === 1 ? "" : "s"}
              </span>
              <span className="rounded-md border px-2 py-1">
                {purchaseCount} purchase{purchaseCount === 1 ? "" : "s"}
              </span>
              <span className="rounded-md border px-2 py-1">
                {entitlementTotal} entitlement{entitlementTotal === 1 ? "" : "s"}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild className="gap-2">
                <Link to={`/catalog/digital/${digitalResourceId}/edit`}>
                  <Settings2 className="h-4 w-4" />
                  Pricing rules
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild className="gap-2">
                <Link to={`/catalog/paid-purchases?${qs}`}>
                  <Receipt className="h-4 w-4" />
                  All purchases
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild className="gap-2">
                <Link to={`/catalog/paid-entitlements?${qs}`}>
                  <Users className="h-4 w-4" />
                  All entitlements
                </Link>
              </Button>
            </div>
            {entitlements.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-foreground">Recent entitlements</p>
                <PaidAccessEntitlementRows
                  rows={entitlements}
                  revokingId={revokingId}
                  onRevoke={revoke}
                  showResourceTitle={false}
                />
              </div>
            )}
          </>
        )}
        <ConfirmDialogComponent />
      </CardContent>
    </Card>
  );
}
